import { useMemo } from 'react'
import {
    applyCommentsViewFilters,
    getCommentsDashboardCommonFilterDefinitions,
    getCommentsDashboardDomainComments,
    getSelectedFilterOptionIds,
} from './CommentsView.filters'
import { applyCommentsViewSearch } from './CommentsView.helpers'
import {
    COMMENTS_VIEW_DOMAIN_TAB_IDS,
    CommentsViewAllFilterGroup,
    CommentsViewAppliedFilterEntry,
    CommentsViewComment,
    CommentsViewCommonFilterDefinition,
    CommentsViewDateRangeFiltersState,
    CommentsViewDomainFilterDefinition,
    CommentsViewDomainTabId,
    CommentsViewFilterConfigData,
    CommentsViewFiltersState,
    CommentsViewTab,
    CommentsViewTabId,
} from './CommentsView.types'

export type CommentsViewDerivedTabState = {
    commentsForTab: CommentsViewComment[]
    filteredComments: CommentsViewComment[]
    commonFilterEntries: CommentsViewAppliedFilterEntry<CommentsViewCommonFilterDefinition>[]
    domainFilterConfigsForTab: CommentsViewFilterConfigData[]
    allFilterGroups: CommentsViewAllFilterGroup[]
    activeDomainTabId: CommentsViewDomainTabId | null
    hasCommentsForTab: boolean
    hasFilteredComments: boolean
    activeCommonFiltersCount: number
    activeDomainFiltersCountForTab: number
    hasActiveFilters: boolean
    visibleCount: number
}

type UseCommentsViewDerivedDataArgs = {
    comments: CommentsViewComment[]
    tabs: CommentsViewTab[]
    selectedTabId: CommentsViewTabId
    filtersState: CommentsViewFiltersState
    dateRange: CommentsViewDateRangeFiltersState
    searchQuery: string
    domainFilterDefinitions: Partial<Record<CommentsViewDomainTabId, CommentsViewDomainFilterDefinition[]>>
    domainTabLabels: Record<CommentsViewDomainTabId, string>
}

const toFilterConfigData = <TDefinition extends { id: string; label: string; placeholder: string }>(
    entries: CommentsViewAppliedFilterEntry<TDefinition>[]
): CommentsViewFilterConfigData[] =>
    entries.map(({ definition, options, selectedOptionIds }) => ({
        id: definition.id,
        label: definition.label,
        placeholder: definition.placeholder,
        options,
        selectedOptionIds,
        isActive: selectedOptionIds.length !== options.length,
    }))

export const useCommentsViewDerivedData = ({
    comments,
    tabs,
    selectedTabId,
    filtersState,
    dateRange,
    searchQuery,
    domainFilterDefinitions,
    domainTabLabels,
}: UseCommentsViewDerivedDataArgs) => {
    const domainCommentsById = useMemo(
        () =>
            COMMENTS_VIEW_DOMAIN_TAB_IDS.reduce<Record<CommentsViewDomainTabId, CommentsViewComment[]>>(
                (acc, domainTabId) => {
                    acc[domainTabId] = getCommentsDashboardDomainComments(comments, domainTabId)
                    return acc
                },
                {} as Record<CommentsViewDomainTabId, CommentsViewComment[]>
            ),
        [comments]
    )

    const { domainFilterEntriesByTabId, domainFiltersByTabId, allFilterGroups } = useMemo(() => {
        const domainFilterEntriesByTabId = COMMENTS_VIEW_DOMAIN_TAB_IDS.reduce<
            Partial<
                Record<CommentsViewDomainTabId, CommentsViewAppliedFilterEntry<CommentsViewDomainFilterDefinition>[]>
            >
        >((acc, domainTabId) => {
            acc[domainTabId] = (domainFilterDefinitions[domainTabId] ?? [])
                .map((definition) => {
                    const options = definition.getOptions(domainCommentsById[domainTabId])
                    return {
                        definition,
                        options,
                        selectedOptionIds: getSelectedFilterOptionIds(
                            options,
                            filtersState.domains[domainTabId]?.[definition.id] ?? []
                        ),
                    }
                })
                .filter(({ options }) => options.length > 0)
            return acc
        }, {})

        const domainFiltersByTabId = COMMENTS_VIEW_DOMAIN_TAB_IDS.reduce<
            Partial<
                Record<
                    CommentsViewDomainTabId,
                    Array<{ definition: CommentsViewDomainFilterDefinition; selectedOptionIds: string[] }>
                >
            >
        >((acc, domainTabId) => {
            acc[domainTabId] = (domainFilterEntriesByTabId[domainTabId] ?? []).map(
                ({ definition, selectedOptionIds }) => ({ definition, selectedOptionIds })
            )
            return acc
        }, {})

        const allFilterGroups: CommentsViewAllFilterGroup[] = COMMENTS_VIEW_DOMAIN_TAB_IDS.map((domainTabId) => ({
            id: domainTabId,
            label: domainTabLabels[domainTabId],
            filters: (domainFilterEntriesByTabId[domainTabId] ?? []).map(
                ({ definition, options, selectedOptionIds }) => ({
                    id: definition.id,
                    label: definition.label,
                    placeholder: definition.placeholder,
                    options,
                    selectedOptionIds,
                })
            ),
        })).filter((group) => group.filters.length > 0)

        return { domainFilterEntriesByTabId, domainFiltersByTabId, allFilterGroups }
    }, [domainCommentsById, domainFilterDefinitions, domainTabLabels, filtersState.domains])

    const derivedTabStateById = useMemo(
        () =>
            tabs.reduce<Record<CommentsViewTabId, CommentsViewDerivedTabState>>(
                (acc, tab) => {
                    const commentsForTab = tab.id === 'all' ? comments : domainCommentsById[tab.id]

                    const commonFilterEntries = getCommentsDashboardCommonFilterDefinitions({
                        getTypeLabel: tab.getTypeLabel,
                    })
                        .map((definition) => {
                            const options = definition.getOptions(comments)
                            return {
                                definition,
                                options,
                                selectedOptionIds: getSelectedFilterOptionIds(
                                    options,
                                    filtersState.common[definition.id] ?? []
                                ),
                            }
                        })
                        .filter(({ options }) => options.length > 0)

                    const filteredComments = applyCommentsViewSearch(
                        applyCommentsViewFilters({
                            comments: commentsForTab,
                            activeTabId: tab.id,
                            dateRange,
                            commonFilters: commonFilterEntries.map(({ definition, selectedOptionIds }) => ({
                                definition,
                                selectedOptionIds,
                            })),
                            domainFiltersByTabId,
                        }),
                        searchQuery,
                        tab.getTypeLabel,
                        tab.getLocationLabel
                    )

                    if (tab.id !== selectedTabId) {
                        acc[tab.id] = {
                            commentsForTab,
                            filteredComments,
                            commonFilterEntries: [],
                            domainFilterConfigsForTab: [],
                            allFilterGroups: [],
                            activeDomainTabId: null,
                            hasCommentsForTab: commentsForTab.length > 0,
                            hasFilteredComments: filteredComments.length > 0,
                            activeCommonFiltersCount: 0,
                            activeDomainFiltersCountForTab: 0,
                            hasActiveFilters: false,
                            visibleCount: filteredComments.length,
                        }
                        return acc
                    }

                    const domainFilterConfigsForTab =
                        tab.id === 'all' ? [] : toFilterConfigData(domainFilterEntriesByTabId[tab.id] ?? [])
                    const activeCommonFiltersCount =
                        commonFilterEntries.filter(
                            ({ selectedOptionIds, options }) => selectedOptionIds.length !== options.length
                        ).length +
                        Number(!!dateRange.from) +
                        Number(!!dateRange.to)
                    const activeDomainFiltersCountForTab = domainFilterConfigsForTab.filter((f) => f.isActive).length
                    const hasActiveDomainFiltersForAll = allFilterGroups.some((group) =>
                        group.filters.some((f) => f.selectedOptionIds.length !== f.options.length)
                    )

                    acc[tab.id] = {
                        commentsForTab,
                        filteredComments,
                        commonFilterEntries,
                        domainFilterConfigsForTab,
                        allFilterGroups,
                        activeDomainTabId: tab.id === 'all' ? null : tab.id,
                        hasCommentsForTab: commentsForTab.length > 0,
                        hasFilteredComments: filteredComments.length > 0,
                        activeCommonFiltersCount,
                        activeDomainFiltersCountForTab,
                        hasActiveFilters:
                            commonFilterEntries.some(
                                ({ selectedOptionIds, options }) => selectedOptionIds.length !== options.length
                            ) ||
                            !!dateRange.from ||
                            !!dateRange.to ||
                            (tab.id === 'all'
                                ? hasActiveDomainFiltersForAll
                                : domainFilterConfigsForTab.some((f) => f.isActive)),
                        visibleCount: filteredComments.length,
                    }

                    return acc
                },
                {} as Record<CommentsViewTabId, CommentsViewDerivedTabState>
            ),
        [
            allFilterGroups,
            comments,
            dateRange,
            domainCommentsById,
            domainFilterEntriesByTabId,
            domainFiltersByTabId,
            filtersState.common,
            searchQuery,
            selectedTabId,
            tabs,
        ]
    )

    return { derivedTabStateById }
}
